import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { debugSlice, type DebugSliceProps } from './slices/debug';
import { regionInfoSlice, type RegionInfoSliceProps } from './slices/region-info';
import { searchSlice, type SearchSliceProps } from './slices/search';
import { popupSlice, type PopupSliceProps } from './slices/popup';
import { userSlice, type UserSlice } from './slices/user';
import { theme, type ThemeSliceProps } from './slices/theme';
import { loadingSlice, type LoadingSliceProps } from './slices/loading';
import { headerSlice, type UiSliceProps } from './slices/header';
import { globalsSlice, type GlobalsSliceProps } from './slices/globals';
import { trackingSlice, type TrackingSliceProps } from './slices/tracking';
import { timerSlice, type TimerSliceProps } from './slices/timer';
import { realityCheckSlice, type RealityCheckSliceProps } from './slices/reality-check';
import { gameWindowsSlice, type GameWindowsSliceProps } from './slices/game-windows';
import { siteWideJackpotSlice, type SiteWideJackpotSliceProps } from './slices/site-wide-jackpot';
import type { RewardMachineSliceProps } from './slices/reward-machine';
import { rewardMachineSlice } from './slices/reward-machine';
import { uiTogglesSlice, type UiTogglesSliceProps } from '@/store/slices/ui-toggles';
import { accountHubModalsSlice, type AccountHubModalsSliceProps } from './slices/account-hub-modal';
import { dynamicOrderingSlice, type DynamicOrderingSliceProps } from './slices/dynamic-ordering';
import { remindersSlice, type RemindersSliceProps } from './slices/reminders';

export type RootState = GlobalsSliceProps &
    DebugSliceProps &
    RegionInfoSliceProps &
    SearchSliceProps &
    PopupSliceProps &
    UserSlice &
    ThemeSliceProps &
    LoadingSliceProps &
    UiSliceProps &
    TrackingSliceProps &
    TimerSliceProps &
    RealityCheckSliceProps &
    GameWindowsSliceProps &
    SiteWideJackpotSliceProps &
    RewardMachineSliceProps &
    UiTogglesSliceProps &
    DynamicOrderingSliceProps &
    RemindersSliceProps &
    AccountHubModalsSliceProps;

export type SetState<T> = {
    set: (fn: (state: T) => {}, replace?: false, actionName?: string) => void;
    get: () => RootState;
};

export const useStore = create<RootState>()(
    devtools(
        (set, get) => ({
            ...globalsSlice({ set, get }),
            ...debugSlice({ set, get }),
            ...regionInfoSlice({ set, get }),
            ...searchSlice({ set, get }),
            ...popupSlice({ set, get }),
            ...userSlice({ set, get }),
            ...theme({ set, get }),
            ...loadingSlice({ set, get }),
            ...headerSlice({ set, get }),
            ...trackingSlice({ set, get }),
            ...timerSlice({ set, get }),
            ...realityCheckSlice({ set, get }),
            ...gameWindowsSlice({ set, get }),
            ...siteWideJackpotSlice({ set, get }),
            ...rewardMachineSlice({ set, get }),
            ...uiTogglesSlice({ set, get }),
            ...dynamicOrderingSlice({ set, get }),
            ...remindersSlice({ set, get }),
            ...accountHubModalsSlice({ set, get })
        }),
        {
            name: 'CasinoStore',
            stateSanitizer: (state: RootState) => (state.gameWindows ? { ...state, gameWindows: '<<OMITTED>>' } : state)
        }
    )
);
